// Live-location consent for on-duty position reporting.
//
// The guard is asked exactly once (on their first authenticated session on this
// device). The answer is stored locally and drives whether
// `useLiveTracking` reports positions while the guard is clocked in.
// The browser/OS permission prompt is never bypassed — "Allow" here only leads
// to the real prompt.
import { useEffect, useState } from "react";

export type LiveLocationConsent = "unset" | "granted" | "declined";

const STORAGE_KEY = "mr.liveLocationConsent.v1";
const EVENT = "mr:live-location-consent";

function keyFor(userId?: string | null) {
  return userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;
}

export function getLiveLocationConsent(userId?: string | null): LiveLocationConsent {
  if (typeof window === "undefined") return "unset";
  try {
    const v = localStorage.getItem(keyFor(userId));
    return v === "granted" || v === "declined" ? v : "unset";
  } catch {
    return "unset";
  }
}

export function setLiveLocationConsent(value: LiveLocationConsent, userId?: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (value === "unset") localStorage.removeItem(keyFor(userId));
    else localStorage.setItem(keyFor(userId), value);
  } catch { /* private mode — session-only consent */ }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { userId, value } }));
}

export function useLiveLocationConsent(userId?: string | null) {
  const [consent, setConsent] = useState<LiveLocationConsent>(() => getLiveLocationConsent(userId));

  useEffect(() => {
    setConsent(getLiveLocationConsent(userId));
    const sync = () => setConsent(getLiveLocationConsent(userId));
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [userId]);

  const update = (value: LiveLocationConsent) => {
    setLiveLocationConsent(value, userId);
    setConsent(value);
  };

  return [consent, update] as const;
}
